import React from 'react'
import styled from '@emotion/styled'
import { Field } from './Field'
import { MultiButton, LabelPosition } from '../buttons'
import { NumericInput, NumericInputProps } from './NumericInput'
import { getColour, getBorderColour, BorderModifier, BaseInputProps, Focusable } from './BaseInput'
import { canStepUp, stepUp, canStepDown, stepDown } from './NumericStepUtils'
import { CORE_PRIMARY } from 'view/styles/constants'

const NumericSpinnerContainer = styled.div`
  display: flex;
`

const NumericInputStyled = styled(NumericInput)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  width: 52px;
  z-index: 1;
`

const SpinnerButton = styled(MultiButton)<BaseInputProps>`
  color: ${props => getColour(props)};
  border-color: ${props => getBorderColour(props, BorderModifier.Normal)};
  ${NumericSpinnerContainer}:focus-within & {
    border-color: ${props => getBorderColour(props, BorderModifier.Focused)};
  }
  ${NumericSpinnerContainer}:not(:focus-within) &:hover {
    border-color: ${props => getBorderColour(props, BorderModifier.Hover)};
    z-index: 2;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  margin-left: -1px;
  border-width: 1px;
  border-style: solid;
  width: 26px;
  height: 26px;
  line-height: 18px;
  font-size: 18px;
  background: ${CORE_PRIMARY};
`

export class NumericSpinner extends React.PureComponent<NumericInputProps> implements Focusable {
  private inputRef = React.createRef<NumericInput>()

  public setFocus() {
    if (this.inputRef.current) {
      this.inputRef.current.setFocus()
    }
  }

  render() {
    const { id, className, ...rest } = this.props
    const { readOnly, disabled, validationIssues, dataTestId } = this.props

    return (
      <NumericSpinnerContainer id={id} className={className}>
        <NumericInputStyled ref={this.inputRef} {...rest} />
        <SpinnerButton
          dataTestId={`${dataTestId}-MinusButton`}
          disabled={disabled || readOnly}
          validationIssues={validationIssues}
          readOnly={false}
          canFocus={false}
          onClick={this.handleStepDown}>
          &minus;
        </SpinnerButton>
        <SpinnerButton
          dataTestId={`${dataTestId}-PlusButton`}
          disabled={disabled || readOnly}
          validationIssues={validationIssues}
          readOnly={false}
          canFocus={false}
          onClick={this.handleStepUp}>
          +
        </SpinnerButton>
      </NumericSpinnerContainer>
    )
  }

  handleStepUp = () => {
    const { onUpdate, value, disabled, readOnly, step, min, max } = this.props

    if (!disabled && !readOnly && canStepUp(this.props.value, { min, max })) {
      const steppedValue = stepUp(value, step || 1, { min, max })
      onUpdate(steppedValue)
    }

    if (this.inputRef.current) {
      this.inputRef.current.setFocus()
    }
  }

  handleStepDown = () => {
    const { onUpdate, value, disabled, readOnly, step, min, max } = this.props

    if (!disabled && !readOnly && canStepDown(this.props.value, { min, max })) {
      const steppedValue = stepDown(value, step || 1, { min, max })
      onUpdate(steppedValue)
    }

    if (this.inputRef.current) {
      this.inputRef.current.setFocus()
    }
  }
}

export const NumericSpinnerField = Field(NumericSpinner, { defaultLabelPosition: LabelPosition.Top })
